import { Progress } from '@heroui/react';
import { Svg } from '@/shared/ui/Svg';
import { formatFileSize } from '@/shared/utils';
import UploadIcon from '@/shared/assets/icons/files/upload-video.svg';
import type { StepScormUpload, StepScormUploadStatus as UploadStatus } from '../model/types';
import { isActiveScormUpload } from '../model/scormUploadsStore';

const STATUS_TEXT: Record<UploadStatus, string> = {
  queued: 'Waiting for other uploads to finish...',
  preparing: 'Preparing for upload...',
  uploading: 'Uploading file...',
  paused: 'No connection. The upload will continue when you are back online',
  completing: 'Finishing the upload...',
  uploaded: 'Uploaded. The SCORM package is being processed...',
  error: 'Upload failed',
};

const PROGRESS_STATUSES: UploadStatus[] = [
  'uploading',
  'paused',
  'completing',
  'uploaded',
  'error',
];

const getStatusText = (upload: StepScormUpload) => {
  if (upload.status === 'error') return upload.error ?? STATUS_TEXT.error;
  // Тот же файл выбран заново: уходят только недостающие части, прогресс начинается не с нуля
  if (upload.status === 'uploading' && upload.resumed) {
    return 'Continuing the upload from where it stopped...';
  }
  return STATUS_TEXT[upload.status];
};

interface StepScormUploadStatusProps {
  upload: StepScormUpload;
  onCancel: () => void;
  onRetry: () => void;
}

/** Загрузка SCORM-пакета шага с этого компьютера: прогресс, статус, отмена и повтор */
export const StepScormUploadStatus = ({
  upload,
  onCancel,
  onRetry,
}: StepScormUploadStatusProps) => {
  const extensionAt = upload.fileName.lastIndexOf('.');
  const format = extensionAt > 0 ? upload.fileName.slice(extensionAt + 1).toUpperCase() : '';
  const isError = upload.status === 'error';
  // Пока бэкенд собирает файл, отменять поздно: пакет уходит в обработку
  const canCancel = isActiveScormUpload(upload) && upload.status !== 'completing';

  return (
    <div className="flex flex-col gap-3">
      <div className="border border-gray-200 rounded-lg bg-white pt-3 pb-2 px-4">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-[#211D55] rounded-[8px] flex items-center justify-center flex-shrink-0">
            <Svg Icon={UploadIcon} width={24} height={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate text-[#293056] tracking-[-0.5%]">
              {upload.fileName}
            </p>
            <p className="text-xs font-medium text-[#9CA3AF] tracking-[-0.5%]">
              {format && <span className="mr-3">Format: {format}</span>}
              Size: {formatFileSize(upload.fileSize)}
            </p>
          </div>
        </div>
        {PROGRESS_STATUSES.includes(upload.status) && (
          <>
            <Progress
              value={upload.progress}
              className="mt-[10px]"
              size="md"
              aria-label={`Upload progress: ${upload.progress}%`}
              classNames={{
                track: 'bg-[#ededed] h-[7px] rounded-[16px]',
                indicator: isError
                  ? 'bg-red-tertiary rounded-[16px]'
                  : 'bg-[#211D55] rounded-[16px]',
              }}
            />
            <p className="text-xs text-[#9CA3AF] tracking-[-0.5%] text-right">{upload.progress}%</p>
          </>
        )}
        <p
          className={
            isError ? 'mt-1 text-sm text-red-tertiary' : 'mt-1 text-sm text-grey-secondary'
          }
          role="status"
        >
          {getStatusText(upload)}
        </p>
      </div>
      {canCancel && (
        <button
          type="button"
          className="w-fit cursor-pointer text-sm text-red-tertiary hover:underline"
          onClick={onCancel}
        >
          Cancel upload
        </button>
      )}
      {isError && (
        <div className="flex items-center gap-4">
          {upload.retry && (
            <button
              type="button"
              className="w-fit cursor-pointer text-sm font-medium text-[#211D55] hover:underline"
              onClick={onRetry}
            >
              {upload.retry === 'resume' ? 'Resume upload' : 'Try again'}
            </button>
          )}
          <button
            type="button"
            className="w-fit cursor-pointer text-sm text-red-tertiary hover:underline"
            onClick={onCancel}
          >
            {upload.ownsPackage ? 'Remove package' : 'Dismiss'}
          </button>
        </div>
      )}
    </div>
  );
};
